import {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import {
  Button, Label,
  RecordInterface, GlobalValuesTypes, SetFormValues,
} from '@components/common/Forms';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import {
  useJobInterviewQuestions,
} from '@components/templates/job-add/edit/hooks/useJobInterviewQuestions';
import { JobInterviewQuestion } from '@components/templates/job-add/edit/form/partials/JobInterviewQuestion';
import { dataTestId } from '@services/data-test-id';
import { MirrorData, SetMirrorData } from '@type/job-add';
import { MAX_ENABLED_INTERVIEW_QUESTIONS } from 'src/constants';

/** Props */
interface JobInterviewQuestionsProps {
  record: RecordInterface,
  getFieldValue: (name: string) => GlobalValuesTypes,
  setFormValues: SetFormValues,
  updateFormElements?: () => void;
  setInterviewQuestionList?: Dispatch<SetStateAction<ApiSchema<'InterviewQuestion'>[] | undefined>>;
  initialInterviewQuestions?: ApiSchema<'InterviewQuestion'>[];
  reqMirrorData?: Pick<MirrorData, 'title' | 'description'>,
  setMirrorData?: SetMirrorData,
  locale: Locale,
  shouldFetchQuestions?:boolean,
}

/**
 * @description Job Interview Questions list component.
 * - `record` - record interface from useBjForm
 * - `getFieldValue` - get fields value function from useBjForm
 * - `updateFormElements` - update form elements function from useBjForm
 * - `initialInterviewQuestions` - initial interview questions;
 * - `setInterviewQuestionList` - set interview question list function
 */
export const JobInterviewQuestions = (props: JobInterviewQuestionsProps) => {
  // Destructure props
  const {
    record,
    getFieldValue,
    updateFormElements,
    initialInterviewQuestions,
    setInterviewQuestionList,
    shouldFetchQuestions,
    reqMirrorData,
    setMirrorData,
    locale,
    setFormValues,
  } = props;

  // Translation
  const { t } = useTranslation('job-add');
  const inputName = 'interviewQuestions';

  // Interview questions hook
  const {
    interviewRecordOptions,
    interviewQuestions,
    setInterviewQuestions,
    addQuestion,
    deleteQuestion,
    saveQuestions,
  } = useJobInterviewQuestions({
    locale,
    setFormValues,
    inputName,
    getFieldValue,
    updateFormElements,
    // have at least an empty field, just like all the rest of the form's elements
    initialInterviewQuestions: initialInterviewQuestions && initialInterviewQuestions?.length > 0
      ? initialInterviewQuestions
      : [{ question: '', enabled: true, readOnly: false }],
    setInterviewQuestionList,
    shouldFetchQuestions,
    reqMirrorData,
    setMirrorData,
  });

  // State for enabled count + message
  const [enabledCount, setEnabledCount] = useState(0);
  const [limitMessage, setLimitMessage] = useState('');

  // Initialize enabled count based on current interviewQuestions
  useEffect(() => {
    const count = interviewQuestions?.filter((q) => q?.enabled)?.length ?? 0;
    setEnabledCount(count);
    if (count < MAX_ENABLED_INTERVIEW_QUESTIONS) {
      setLimitMessage('');
    }
  }, [interviewQuestions]);

  // Render
  // **************************
  return (
    <Label
      as="div"
      className="!mt-0"
      description={t('job.add.label.questions')}
      descriptionClass="font-bold text-ink-medium px-3.5"
    >
      {/* Description tip */}
      <span className="block px-3.5 text-sm leading-snug text-ink-light">
        {t('job.add.label.job_questions_help')}
      </span>

      <div className="mt-3 grid grid-cols-1 justify-items-start gap-4">
        {/* Questions */}
        {interviewRecordOptions.map((question, index) => (
          <JobInterviewQuestion
            key={`${question.id}-${question.defaultValue || index}`}
            record={record}
            recordOptions={question}
            saveQuestions={saveQuestions}
            deleteQuestion={() => deleteQuestion(index)}
            inputName={inputName}
            showDeleteButton={interviewRecordOptions.length > 1}
            currentIndex={index}
            enabled={interviewQuestions?.[index]?.enabled ?? true}
            readOnly={interviewQuestions?.[index]?.readOnly ?? false}
            onToggleEnabled={(checked) => {
              setInterviewQuestions((prev) => {
                const list = [...(prev ?? [])];
                if (!list[index]) return list;

                const currentCount = list.filter((q) => q?.enabled).length;

                if (checked && currentCount >= MAX_ENABLED_INTERVIEW_QUESTIONS) {
                  setLimitMessage(t('global-label-maximum-3-question'));
                  return list;
                }

                // If below limit, clear any old message
                setLimitMessage('');

                list[index] = { ...list[index], enabled: checked } as ApiSchema<'InterviewQuestion'>;
                return list;
              });
            }}

          />
        ))}

        {limitMessage && (
          <p className="mt-2 text-sm text-red-500">{limitMessage}</p>
        )}

        {/* Add question button. Max 5 questions */}
        {(enabledCount < MAX_ENABLED_INTERVIEW_QUESTIONS) && (
          <Button
            color="light"
            styling="soft"
            size="sm"
            rounding="full"
            className="gap-1 !py-1.5 !pl-3 !pr-4 !font-normal leading-tight"
            onClick={addQuestion}
            {...dataTestId('jobAddPage.jobInterviewQuestions.addButton')}
          >
            <PlusCircleIcon className="size-4" />
            {t('job.add.collection_add.questions')}
          </Button>
        )}
      </div>
    </Label>
  );
};
